import React, { useRef } from 'react';
import Moment from 'react-moment';
import { AiFillPrinter } from 'react-icons/ai';
import styled from 'styled-components';
import ReactToPrint from 'react-to-print';


export const Wrapper = styled.div`
  padding: 0; /* No padding to maximize content area */
  font-size: 1.3rem;
  font-weight: bold;
  text-transform: capitalize;
  @media print {
    padding: 0;
    font-size: 1.5rem; /* Base font size: 1rem + 0.2rem */
    width: 100%;
    box-sizing: border-box;
    margin: 0;
    @page {
      size: A4; /* Use A4; change to Letter for US if needed */
      margin: 5mm; /* Minimal margins */
    }
    /* Ensure content fits and doesn't overflow */
    * {
      overflow: visible !important;
      break-inside: avoid;
      box-sizing: border-box;
    }
    /* Maximize table width */
    table, div {
      width: 100% !important;
      margin: 0 !important;
    }
  }
`;

export const Flexed = styled.div`
  width: ${props => props.width || '100%'};
  display: flex;
  justify-content: ${props => props.justifycontent || 'flex-start'};
  align-items: ${props => props.alignItems || 'flex-start'};
  position: ${props => props.position || 'relative'};
  padding: ${props => props.padding || '3px 0px'};
  bottom: ${props => props.bottom || ''};
  font-size: ${props => props.fontSize || ''};
  @media print {
    width: 100%;
    flex-wrap: nowrap; /* Prevent wrapping to keep columns in a single row */
    margin: 0;
    padding: 0; /* Remove padding to align flush with left */
  }
`;

export const Flex = styled.div`
  width: ${props => props.width || 'auto'};
  position: ${props => props.position || 'relative'};
  flex: ${props => props.flex || '1'};
  white-space: ${props => props.whiteSpace || 'normal'};
  border-bottom: ${props => props.border ? '1px solid black' : ''};
  text-align: ${props => props.textAlign || 'left'};
  padding: ${props => props.padding || '0'};
  font-size: ${props => props.fontSize || ''};
  overflow: hidden; /* Prevent text overflow */
  text-overflow: ellipsis; /* Truncate long text */
  @media print {
    flex: ${props => props.flex || 'auto'}; /* Allow columns to adjust dynamically */
    min-width: 0; /* Prevent flex items from growing too large */
    white-space: normal; /* Allow text wrapping for print */
  }

  h2 {
    margin: 0;
    padding: 0;
  }
`;

export default function PrintComponent({ csrjobs, title, salesreps, columnVisibility }) {
  const componentRef = useRef();

  return (
    <Flex id="print_component">
      <ReactToPrint
        trigger={() => <AiFillPrinter style={{ fontSize: 'calc(1.4rem + .5vw)', cursor: 'pointer' }} />}
        content={() => componentRef.current}
      />
      <Flex style={{ display: 'none' }}>
        <ComponentToPrint
          salesreps={salesreps}
          csrjobs={csrjobs}
          title={title}
          columnVisibility={columnVisibility}
          ref={componentRef}
        />
      </Flex>
    </Flex>
  );
}

class ComponentToPrint extends React.Component {
  render() {
    const { csrjobs, title, salesreps, columnVisibility } = this.props;

    // Calculate visible columns for dynamic flex distribution
    const visibleColumns = Object.values(columnVisibility).filter(Boolean).length;
    const baseFlex = 100 / visibleColumns; // Distribute width evenly

    return (
      <Wrapper>
        <Flexed
          justifycontent="center"
          style={{ fontSize: '1.7rem', textAlign: 'center', padding: '8px 0' }} // 1.5rem + 0.2rem
        >
          {title}
        </Flexed>
        <Flexed
          style={{
            borderBottom: '1px solid black',
            padding: '4px',
            background: '#8398AB',
            color: 'white',
            fontSize: '2rem' // 1.2rem + 0.2rem
          }}
        >
          {columnVisibility.job && <Flex padding="0px 0px 0px 0px" style={{ flex: `${baseFlex}%`,fontSize: '1.7rem' }}>Job#</Flex>}
          {columnVisibility.customer && <Flex style={{ flex: `${baseFlex * 1.5}%`,fontSize: '1.7rem'  }}>Customer</Flex>}
          {columnVisibility.jobname && <Flex style={{ flex: `${baseFlex * 2}%`,fontSize: '1.7rem'  }}>Job Name</Flex>}
          {columnVisibility.salesrep && <Flex style={{ flex: `${baseFlex * 1.5}%`,fontSize: '1.7rem'  }}>Sales Rep</Flex>}
          {columnVisibility.quantity && <Flex style={{ flex: `${baseFlex}%`,fontSize: '1.7rem'  }}>Quantity</Flex>}
          {columnVisibility.dateentered && <Flex style={{ flex: `${baseFlex}%`,fontSize: '1.7rem'  }}>Entered</Flex>}
          {columnVisibility.duedate && <Flex style={{ flex: `${baseFlex}%`,fontSize: '1.7rem'  }}>Due</Flex>}
          {columnVisibility.csrSalesRep && <Flex style={{ flex: `${baseFlex}%`,fontSize: '1.7rem'  }}>Sales Rep (CSR)</Flex>}
          {columnVisibility.status && <Flex style={{ flex: `${baseFlex * 1.5}%`,fontSize: '1.7rem'  }}>Status</Flex>}
        </Flexed>
        <div style={{ width: '100%' }}> {/* No border */}
          {csrjobs?.map(job => (
            <Flexed key={job.job} style={{ padding: '4px', fontSize: '1.2rem'  }}> {/* 1rem + 0.2rem */}
              {columnVisibility.job && <Flex style={{ flex: `${baseFlex}%`,fontSize: '1.4rem' }}>{job.job}</Flex>}
              {columnVisibility.customer && <Flex style={{ flex: `${baseFlex * 1.5}%`,fontSize: '1.4rem' }}>{job.customer}</Flex>}
              {columnVisibility.jobname && (
                <Flex style={{ flex: `${baseFlex * 2}%`, paddingLeft: '10px',fontSize: '1.4rem' }}>{job.jobname}</Flex>
              )}
              {columnVisibility.salesrep && (
                <Flex style={{ flex: `${baseFlex * 1.5}%`,fontSize: '1.4rem' }}>
                  {salesreps?.find(rep => rep.empId === job.salesrep)?.firstName + ' ' +
                   salesreps?.find(rep => rep.empId === job.salesrep)?.lastName?.substr(0, 1) || 'N/A'}
                </Flex>
              )}
              {columnVisibility.quantity && <Flex style={{ flex: `${baseFlex}%`,fontSize: '1.4rem' }}>{job.quantity || job.origquantity}</Flex>}
              {columnVisibility.dateentered && (
                <Flex style={{ flex: `${baseFlex}%`, }}><Moment style={{fontSize: '1.4rem'}}format="MM/DD/YYYY">{job.dateentered}</Moment></Flex>
              )}
              {columnVisibility.duedate && (
                <Flex style={{ flex: `${baseFlex}%` }}><Moment style={{fontSize: '1.4rem'}} format="MM/DD/YYYY">{job.duedate}</Moment></Flex>
              )}
              {columnVisibility.csrSalesRep && (
                <Flex style={{ flex: `${baseFlex}%`,fontSize: '1.4rem' }}>
                  {salesreps?.filter(rep => rep.empId === job.salesrep).map(rep => (
                    rep.firstName + ' ' + rep.lastName.substr(0, 1)
                  ))}
                </Flex>
              )}
              {columnVisibility.status && <Flex style={{ flex: `${baseFlex * 1.5}%`,fontSize: '1.4rem' }}>{job.test || 'On Incoming'}</Flex>}
            </Flexed>
          ))}
        </div>
      </Wrapper>
    );
  }
}